// 付箋の背景色に応じて、本文・署名の文字色を「読める色」へ切り替えるためのユーティリティ。
//
// 付箋の文字色は従来「黒（#1a1a2e）」固定だったが、利用者が濃い背景色を指定すると本文が
// 背景に埋もれて読めなくなる。そこで背景色と文字色のコントラスト比（WCAG 相対輝度ベース）を
// 計算し、暗い文字より明るい文字（白）の方がコントラストが高い＝読みやすい背景では、文字色を
// 白へ反転させる。これにより付箋がどんな色でも本文が読めるようにする。
//
// 画面表示（sticky-object.tsx）・PDF 変換（canvas-pdf-export.ts）の双方で同じ判定を共有し、
// 印刷・PDF でも見た目どおり（背景に応じた文字色）になるようにする。

import { isValidHexColor, normalizeHexColor } from "./pen-preference";

/** 明るい背景で使う本文色（従来の付箋文字色。実質黒）。 */
export const STICKY_TEXT_COLOR_DARK = "#1a1a2e";
/** 暗い背景で使う本文色（白へ反転）。 */
export const STICKY_TEXT_COLOR_LIGHT = "#ffffff";
/** 明るい背景で使う署名色（従来どおりの薄い黒）。 */
export const STICKY_SIGNATURE_COLOR_DARK = "rgba(0,0,0,0.45)";
/** 暗い背景で使う署名色（薄い白へ反転）。 */
export const STICKY_SIGNATURE_COLOR_LIGHT = "rgba(255,255,255,0.7)";

/** 付箋色未指定時の既定背景色（sticky-object.tsx / canvas-pdf-export.ts と同じ値）。 */
const STICKY_DEFAULT_BACKGROUND = "#fff5b3";

/** 付箋の文字色パレット（背景色から解決した本文色・署名色の組）。 */
export interface StickyTextPalette {
  /** 本文の文字色。 */
  readonly body: string;
  /** 左下署名の文字色。 */
  readonly signature: string;
  /** 明るい文字（白系）へ反転したか（暗い背景と判定したか）。 */
  readonly isLightText: boolean;
}

// sRGB の 1 チャンネル値（0-255）を、相対輝度計算用の線形値へ変換する（WCAG 2.x の定義）。
function channelToLinear(value: number): number {
  const normalized = value / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

// hex 色（#RRGGBB / #RGB）の相対輝度（0=黒 〜 1=白）を返す。
function relativeLuminanceOfHex(hex: string): number {
  const normalized = normalizeHexColor(hex);
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return (
    0.2126 * channelToLinear(r) + 0.7152 * channelToLinear(g) + 0.0722 * channelToLinear(b)
  );
}

// 2 つの相対輝度からコントラスト比（1〜21）を返す（WCAG の定義）。
function contrastRatioOfLuminance(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * 付箋の背景色から、読みやすい本文色・署名色のパレットを解決する。
 *
 * 判定規則:
 * - 背景色に対して「暗い文字（{@link STICKY_TEXT_COLOR_DARK}）」と
 *   「明るい文字（{@link STICKY_TEXT_COLOR_LIGHT}）」のコントラスト比をそれぞれ計算し、
 *   明るい文字の方が高コントラスト（＝読みやすい）場合のみ白へ反転する。
 * - 背景色が hex として解釈できない場合（未指定・named color・rgb() など）は、既定の淡い付箋色を
 *   前提として暗い文字を採用する（安全側。従来挙動を維持）。
 */
export function resolveStickyTextPalette(background: string | undefined): StickyTextPalette {
  const source = isValidHexColor(background) ? background : STICKY_DEFAULT_BACKGROUND;
  const backgroundLuminance = relativeLuminanceOfHex(source);
  const darkTextContrast = contrastRatioOfLuminance(
    backgroundLuminance,
    relativeLuminanceOfHex(STICKY_TEXT_COLOR_DARK)
  );
  const lightTextContrast = contrastRatioOfLuminance(
    backgroundLuminance,
    relativeLuminanceOfHex(STICKY_TEXT_COLOR_LIGHT)
  );
  const isLightText = lightTextContrast > darkTextContrast;
  return {
    body: isLightText ? STICKY_TEXT_COLOR_LIGHT : STICKY_TEXT_COLOR_DARK,
    signature: isLightText ? STICKY_SIGNATURE_COLOR_LIGHT : STICKY_SIGNATURE_COLOR_DARK,
    isLightText,
  };
}
